import React, { useState, useEffect } from 'react';
import { TrussPlane, TrussType } from '../../types/truss';
import { useTrussStore } from '../../hooks/useTrussManagement';
import { DraggablePanel } from '../ui/DraggablePanel';

interface DraggableTrussConfigPanelProps {
  nodeId: string;
  position: { x: number; y: number };
  onClose: () => void;
}

// Session-based persistence utilities (clears on app restart/refresh)
const STORAGE_KEY = 'truss_node_input_values';
const SESSION_TIMESTAMP_KEY = 'truss_session_timestamp';
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes timeout

// Check if current session is valid (not expired)
const isSessionValid = (): boolean => {
  try {
    const timestamp = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const sessionTime = parseInt(timestamp);
    const currentTime = Date.now();
    return (currentTime - sessionTime) < SESSION_TIMEOUT;
  } catch (error) {
    return false;
  }
};

// Update session timestamp
const updateSessionTimestamp = () => {
  try {
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to update session timestamp:', error);
  }
};

const loadNodeInputValues = (): Record<string, {
  rotationValues: { X: number; Y: number; Z: number };
  perpendicularValues: { X: number; Y: number; Z: number };
}> => {
  try {
    // Check if session is still valid
    if (!isSessionValid()) {
      // Session expired or new session, clear old data
      sessionStorage.removeItem(STORAGE_KEY);
      updateSessionTimestamp();
      return {};
    }
    
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load truss input values from sessionStorage:', error);
    return {};
  }
};

const saveNodeInputValues = (values: Record<string, {
  rotationValues: { X: number; Y: number; Z: number };
  perpendicularValues: { X: number; Y: number; Z: number };
}>) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    updateSessionTimestamp();
  } catch (error) {
    console.warn('Failed to save truss input values to sessionStorage:', error);
  }
};

const updateNodeInputValue = (nodeId: string, rotationValues: { X: number; Y: number; Z: number }, perpendicularValues: { X: number; Y: number; Z: number }) => {
  // Only save if there are non-zero values to avoid cluttering storage with default values
  const hasNonZeroValues = Object.values(rotationValues).some(v => v !== 0) || Object.values(perpendicularValues).some(v => v !== 0);
  
  const allValues = loadNodeInputValues();
  
  if (hasNonZeroValues) {
    allValues[nodeId] = { rotationValues, perpendicularValues };
  } else {
    // Remove entry if all values are zero
    delete allValues[nodeId];
  }
  
  saveNodeInputValues(allValues);
};

// Clear all stored values (useful when nodes are reset)
const clearAllNodeInputValues = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to clear truss input values from sessionStorage:', error);
  }
};

export const DraggableTrussConfigPanel: React.FC<DraggableTrussConfigPanelProps> = ({
  nodeId,
  position,
  onClose,
}) => {
  const {
    selectedTrussType,
    setSelectedTrussType,
    addTruss,
    removeTruss,
    getTrussesForNode
  } = useTrussStore();

  // Local state for configuration
  const [plane, setPlane] = useState<TrussPlane>('X');
  const [rotationValues, setRotationValues] = useState({
    X: 0,
    Y: 0,
    Z: 0
  });
  const [perpendicularValues, setPerpendicularValues] = useState({
    X: 0,
    Y: 0,
    Z: 0
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Load persisted values when component mounts
  useEffect(() => {
    // Check if this node has any existing trusses
    const existingRegularTrusses = getTrussesForNode(nodeId, 'regular');
    const existingPerpendicularTrusses = getTrussesForNode(nodeId, 'perpendicular');
    const hasExistingTrusses = existingRegularTrusses.length > 0 || existingPerpendicularTrusses.length > 0;
    
    // If no existing trusses and this is the first node being opened, it might be a fresh session
    if (!hasExistingTrusses) {
      // Check if there are stored values for multiple nodes (indicating active session)
      const allSavedValues = loadNodeInputValues();
      const storedNodeCount = Object.keys(allSavedValues).length;
      
      // If only this node or no nodes have stored values, and no trusses exist, 
      // it's likely a fresh start - don't load old values
      if (storedNodeCount <= 1) {
        clearAllNodeInputValues();
        setIsInitialized(true);
        return;
      }
    }
    
    // Load stored values if available
    const allSavedValues = loadNodeInputValues();
    const savedValues = allSavedValues[nodeId];
    if (savedValues) {
      setRotationValues(savedValues.rotationValues);
      setPerpendicularValues(savedValues.perpendicularValues);
    }
    setIsInitialized(true);
  }, [nodeId, getTrussesForNode]);

  // Save values when component unmounts or values change (but not on first load)
  useEffect(() => {
    if (!isInitialized) return; // Don't save on first load
    updateNodeInputValue(nodeId, rotationValues, perpendicularValues);
  }, [nodeId, rotationValues, perpendicularValues, isInitialized]);

  // Panel no longer assigns visual properties (colors/sizes); arrow visuals
  // are controlled by the dedicated arrow components. This panel only
  // provides numeric input values.

  // Get existing trusses for the node
  const regularTrusses = getTrussesForNode(nodeId, 'regular');
  const perpendicularTrusses = getTrussesForNode(nodeId, 'perpendicular');

  // Handle applying truss (only called when Apply button is clicked)
  const handleApplyTruss = () => {
    // Do not pass color or styling from the UI panel. Arrow components
    // will decide how to render by default. Only pass numeric values.
    if (selectedTrussType === 'regular') {
      const rotationValue = rotationValues[plane];
      // Remove existing truss first if it exists
      const existingTruss = regularTrusses.find(truss => truss.plane === plane);
      if (existingTruss) {
        removeTruss(existingTruss.id);
      }
      
      // Only add truss if value is not zero
      if (rotationValue !== 0) {
        addTruss(nodeId, 'regular', plane, undefined, undefined, rotationValue);
        setSuccessMessage(`Applied Regular Truss to ${plane}-Plane with ${rotationValue >= 0 ? 'Clockwise' : 'Anticlockwise'} ${Math.abs(rotationValue)}°`);
      } else {
        setSuccessMessage(`Removed Regular Truss from ${plane}-Plane`);
      }
    } else {
      const perpendicularValue = perpendicularValues[plane];
      // Remove existing truss first if it exists
      const existingTruss = perpendicularTrusses.find(truss => truss.plane === plane);
      if (existingTruss) {
        removeTruss(existingTruss.id);
      }
      
      // Only add truss if value is not zero
      if (perpendicularValue !== 0) {
        addTruss(nodeId, 'perpendicular', plane, undefined, undefined, perpendicularValue);
        setSuccessMessage(`Applied Perpendicular Truss to ${plane}-Plane with value ${perpendicularValue}`);
      } else {
        setSuccessMessage(`Removed Perpendicular Truss from ${plane}-Plane`);
      }
    }
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Handle removing specific truss when value is set to zero
  // Note: This function is now only used by the Apply button handler
  // const handleRemoveTruss = (plane: TrussPlane, type: TrussType) => {
  //   const trusses = type === 'regular' ? regularTrusses : perpendicularTrusses;
  //   const existingTruss = trusses.find(truss => truss.plane === plane);
  //   if (existingTruss) {
  //     removeTruss(existingTruss.id);
  //     setSuccessMessage(`Removed ${type} truss from ${plane}-Plane`);
  //     setTimeout(() => setSuccessMessage(''), 3000);
  //   }
  // };

  // Auto-remove truss when value is set to zero (but don't auto-add)
  const handlePerpendicularValueChange = (plane: TrussPlane, value: number) => {
    setPerpendicularValues(prev => ({ ...prev, [plane]: value }));
    // Note: Truss removal/addition only happens when Apply button is clicked
  };

  // Auto-remove truss when value is set to zero (but don't auto-add)
  const handleRotationValueChange = (plane: TrussPlane, value: number) => {
    setRotationValues(prev => ({ ...prev, [plane]: value }));
    // Note: Truss removal/addition only happens when Apply button is clicked
  };

  const handleTypeChange = (type: TrussType) => {
    setSelectedTrussType(type);
  };

  const handlePlaneChange = (newPlane: TrussPlane) => {
    setPlane(newPlane);
  };

  const handleClose = () => {
    // Save current values before closing
    updateNodeInputValue(nodeId, rotationValues, perpendicularValues);
    onClose();
  };

  // Check if Apply button should be enabled - always enabled now
  // const canApplyTruss = () => {
  //   return true; // Always allow applying - let the apply handler decide what to do
  // };

  // Get current input value for selected plane and type
  // const getCurrentInputValue = () => {
  //   if (selectedTrussType === 'regular') {
  //     return rotationValues[plane];
  //   } else {
  //     return perpendicularValues[plane];
  //   }
  // };

  return (
    <DraggablePanel
      initialPosition={position}
      title={`Node Load`}
      onClose={handleClose}
      style={{
        width: '400px',
        maxWidth: '90vw',
        maxHeight: '80vh'
      }}
    >
      {/* Scrollable Content Container */}
      <div 
        className="overflow-y-auto overflow-x-hidden draggable-truss-panel" 
        style={{ 
          maxHeight: '60vh',
          minHeight: '300px'
        }}
      >
        {/* Success Message */}
        {successMessage && (
          <div className="mx-4 mt-2 p-3 bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-md">
            <p className="text-sm text-green-800 dark:text-green-200 text-center font-medium">
              {successMessage}
            </p>
          </div>
        )}

        <div className="p-4">
          <div className="space-y-4">
          {/* Type Selection Buttons */}
          <div>
           <label className="block text-sm font-medium mb-2 dark:text-gray-300">
              
            </label>
            <div className="flex">
              <button
                onClick={() => handleTypeChange('regular')}
                className={`flex-1 px-4 py-2 text-sm rounded-l-md transition-colors ${
                  selectedTrussType === 'regular'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                Type 1
              </button>
              <button
                onClick={() => handleTypeChange('perpendicular')}
                className={`flex-1 px-4 py-2 text-sm rounded-r-md transition-colors ${
                  selectedTrussType === 'perpendicular'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                Type 2
              </button>
            </div>
          </div>

          {/* Plane Selection */}
          <div>
            <label className="block text-sm font-medium mb-2 dark:text-gray-300">
              {/* Plane Selection */}
            </label>
            <div className="space-y-3">
              <div 
                className="flex items-center justify-between space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handlePlaneChange('X')}
              >
                <span className={`text-sm font-medium cursor-pointer ${plane === 'X' ? 'text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>
                  DX
                </span>
                {selectedTrussType === 'regular' && (
                  <div className="flex items-center space-x-2">
                    {/* <label className="text-xs text-gray-600 dark:text-gray-400">Rotation:</label> */}
                    <input
                      type="number"
                      value={rotationValues.X}
                      onChange={(e) => handleRotationValueChange('X', parseFloat(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      step="0.1"
                      placeholder="0"
                    />
                  </div>
                )}
                {selectedTrussType === 'perpendicular' && (
                  <div className="flex items-center space-x-2">
                    {/* <label className="text-xs text-gray-600 dark:text-gray-400">Value:</label> */}
                    <input
                      type="number"
                      value={perpendicularValues.X}
                      onChange={(e) => handlePerpendicularValueChange('X', parseFloat(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      step="0.1"
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
              <div 
                className="flex items-center justify-between space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handlePlaneChange('Y')}
              >
                <span className={`text-sm font-medium cursor-pointer ${plane === 'Y' ? 'text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>
                  DY
                </span>
                {selectedTrussType === 'regular' && (
                  <div className="flex items-center space-x-2">
                    {/* <label className="text-xs text-gray-600 dark:text-gray-400">Rotation:</label> */}
                    <input
                      type="number"
                      value={rotationValues.Y}
                      onChange={(e) => handleRotationValueChange('Y', parseFloat(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      step="0.1"
                      placeholder="0"
                    />
                  </div>
                )}
                {selectedTrussType === 'perpendicular' && (
                  <div className="flex items-center space-x-2">
                    {/* <label className="text-xs text-gray-600 dark:text-gray-400">Value:</label> */}
                    <input
                      type="number"
                      value={perpendicularValues.Y}
                      onChange={(e) => handlePerpendicularValueChange('Y', parseFloat(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      step="0.1"
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
              <div 
                className="flex items-center justify-between space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handlePlaneChange('Z')}
              >
                <span className={`text-sm font-medium cursor-pointer ${plane === 'Z' ? 'text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>
                  DZ
                </span>
                {selectedTrussType === 'regular' && (
                  <div className="flex items-center space-x-2">
                    {/* <label className="text-xs text-gray-600 dark:text-gray-400">Rotation:</label> */}
                    <input
                      type="number"
                      value={rotationValues.Z}
                      onChange={(e) => handleRotationValueChange('Z', parseFloat(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      step="0.1"
                      placeholder="0"
                    />
                  </div>
                )}
                {selectedTrussType === 'perpendicular' && (
                  <div className="flex items-center space-x-2">
                    {/* <label className="text-xs text-gray-600 dark:text-gray-400">Value:</label> */}
                    <input
                      type="number"
                      value={perpendicularValues.Z}
                      onChange={(e) => handlePerpendicularValueChange('Z', parseFloat(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      step="0.1"
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
            <button
              onClick={handleApplyTruss}
              className="flex-[2] px-4 py-2 text-sm text-white rounded-md transition-colors bg-blue-500 hover:bg-blue-600"
            >
              Apply
            </button>
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      {/* <div className="p-3 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-b-lg"> */}
        {/* <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          🖱️ Drag the title bar to move this panel anywhere on screen
        </div> */}
      {/* </div> */}
    </DraggablePanel>
  );
};

export default DraggableTrussConfigPanel;
